import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ButtonDS,
  ElectricBoltOutlined,
  IconCircle,
  PendingActionsCard,
  PendingActionTask,
  Spacer,
} from "@qivia/ui";
import {
  PieChartDS,
  PieChartData,
} from "@qivia/ui/src/designSystem/components/Charts/PieChart";
import { CardChart } from "@qivia/ui/src/designSystem/components/Charts/CardChart";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { formatCurrency } from "../../../libSlice/materialUiFormats";
import {
  getElectricChargesAsync,
  selectElectricCharges,
  selectElectricChargesStatus,
} from "./electricChargingSlice";
import {
  getLinkyConsentsAsync,
  selectLinkyConsents,
  selectLinkyConsentsStatus,
} from "./linkyConsentsSlice";
import { ElectricChargeType } from "./electricChargingAPI";
import "../../../i18n/i18n";

const chargeTypeOrder: ElectricChargeType[] = ["STATION", "HOME", "ON_SITE"];

const chargeTypeColors: Record<ElectricChargeType, string> = {
  STATION: colors["colors/accent/500"],
  HOME: colors["colors/accent/900"],
  ON_SITE: colors["colors/system/informative/informative_light"],
};

export const SummaryTab = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const electricCharges = useAppSelector(selectElectricCharges);
  const electricChargesStatus = useAppSelector(selectElectricChargesStatus);
  const linkyConsents = useAppSelector(selectLinkyConsents);
  const linkyConsentsStatus = useAppSelector(selectLinkyConsentsStatus);

  const basePath = location.pathname.split("/")[1];

  useEffect(() => {
    if (electricChargesStatus === "idle") {
      void dispatch(getElectricChargesAsync());
    }
  }, [dispatch, electricChargesStatus]);

  useEffect(() => {
    if (linkyConsentsStatus === "idle") {
      void dispatch(getLinkyConsentsAsync());
    }
  }, [dispatch, linkyConsentsStatus]);

  const { categoryData, total } = useMemo(() => {
    const costByType = electricCharges.reduce(
      (acc, charge) => {
        if (!charge.type) {
          return acc;
        }
        acc[charge.type] = (acc[charge.type] ?? 0) + charge.totalCost;
        return acc;
      },
      {} as Record<ElectricChargeType, number>,
    );

    const total = Object.values(costByType).reduce(
      (acc, cost) => acc + cost,
      0,
    );

    const categoryData: PieChartData[] = chargeTypeOrder
      .filter((type) => costByType[type] !== undefined)
      .map((type) => ({
        name: t(`electric_charging.cost_by_type.${type}`),
        value: total > 0 ? (costByType[type] / total) * 100 : 0,
        color: chargeTypeColors[type],
        amount: formatCurrency(costByType[type]),
      }));

    return { categoryData, total };
  }, [electricCharges, t]);

  const pendingLinkyCount = useMemo(
    () =>
      linkyConsents.filter((consent) => consent.consentStatus === "PENDING")
        .length,
    [linkyConsents],
  );

  const tasks: PendingActionTask[] = useMemo(() => {
    if (pendingLinkyCount === 0) {
      return [];
    }
    return [
      {
        icon: (
          <IconCircle
            icon={<ElectricBoltOutlined />}
            backgroundColor={"warningLight"}
            iconColor={"warningNormal"}
          />
        ),
        title: t("electric_charging.pendingActions.linkyToRelaunch.title", {
          count: pendingLinkyCount,
        }),
        subtitle: t(
          "electric_charging.pendingActions.linkyToRelaunch.subtitle",
        ),
        button: (
          <ButtonDS
            text={t("linky_consents.action.resend")}
            format={"hug"}
            buttonType={"secondary"}
            onClick={() =>
              navigate(
                `/${basePath}/electric_charging/linky_connections?filterStatus=PENDING`,
              )
            }
          />
        ),
      },
    ];
  }, [pendingLinkyCount, t, navigate, basePath]);

  if (params.tab !== "summary") {
    return null;
  }

  // TODO later: wire the CardChart period selector — the donut currently
  // ignores the selected period (mock data is not split by period).
  return (
    <StyledRow>
      <PendingActionsCard
        subject={t("electric_charging.title")}
        tasks={tasks}
        maxWidth={40}
      />
      <Spacer x={2} />
      <CardChart
        title={t("electric_charging.cost_by_type.title")}
        isLineChart={false}
        isVisible={categoryData.length > 0}
      >
        {() => (
          <PieChartDS
            categoryData={categoryData}
            unit="%"
            displayPercentageLegend={true}
            centerLabel={{
              value: formatCurrency(total),
              label: t("electric_charging.cost_by_type.total"),
            }}
          />
        )}
      </CardChart>
    </StyledRow>
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`;
